import { ApiUtility, IApiResponse } from "../Utilities/ApiUtility.axios";
export enum TicketModule {
  Unknown = 0,
  AML = 1,
  Billing = 2,
  Books = 3,
  CRM = 4,
  PAYE = 5,
  SA = 6,
  Accounts = 7,
}
export enum TicketStatus {
  Unknown = 0,
  Open = 1,
  Closed = 2,
  UnderResearch = 3,
  InProgress = 4,
  InTesting = 5,
  ReadyForRelease = 6,
  Active = 7,
}
export enum TicketPriority {
  Unknown = 0,
  P1 = 1,
  P2 = 2,
  P3 = 3,
  P4 = 4,
}

export enum TicketIssueType {
  Unknown = 0,
  Bug = 1,
  New = 2,
  Enhancement = 3,
}

export enum CloseReason {
  Unknown = 0,
  Other,
  IssueFixed,
  ResolvedInLatestRelease,
  NoResolutionRequired,
  RevokedByEndUser,
  CreatedByMistake,
  AmendmentsUpdatedInSoftware,
  ThirdPartyIntegrationResolved,
  DuplicateTicket,
  TechnicalGlitch,
}

export const TicketModuleDisplay: Record<TicketModule, string> = {
  [TicketModule.Unknown]: '-',
  [TicketModule.AML]: 'AML',
  [TicketModule.Billing]: 'Billing & subscriptions',
  [TicketModule.Books]: 'Bookkeeping',
  [TicketModule.CRM]: 'CRM',
  [TicketModule.PAYE]: 'Payroll',
  [TicketModule.SA]: 'Self assessment',
  [TicketModule.Accounts]: 'Tax and accounts',
};

export const DateFormats = [ 'DDMMYY', 'DDMMYYYY', 'DD/MM/YYYY', 'DDMMYYYY', 'DD-MM-YYYY'];

export const CloseReasonDisplay: Record<CloseReason, string> = {
  [CloseReason.Unknown]: 'Unknown',
  [CloseReason.Other]: 'Other',
  [CloseReason.IssueFixed]: 'Issue fixed and resolved',
  [CloseReason.ResolvedInLatestRelease]: 'The resolution has been resolved as part of the latest release.',
  [CloseReason.NoResolutionRequired]: 'No resolution required, the client proceeded with our assistance.',
  [CloseReason.RevokedByEndUser]: 'Revoked by the end user',
  [CloseReason.CreatedByMistake]: 'The ticket was created by mistake.',
  [CloseReason.AmendmentsUpdatedInSoftware]: 'The amendments have been updated in the software.',
  [CloseReason.ThirdPartyIntegrationResolved]: 'The issue persisted within the third-party integration and has now been resolved.',
  [CloseReason.DuplicateTicket]: 'Duplicate ticket',
  [CloseReason.TechnicalGlitch]: 'The issue was due to a technical glitch',
};

export const CLOSED_REASONS = Object.keys(CloseReasonDisplay)
  .map(k => Number(k))
  .filter(k => !isNaN(k) && k !== CloseReason.Unknown)
  .map(k => ({
    label: CloseReasonDisplay[k as CloseReason],
    value: k as CloseReason
  }));

class TicketService {
  route = "api/Tickets";

  create = async (data: any) => {
    return ApiUtility.postForm<IApiResponse<ITicket>>(`${this.route}`, data);
  };



  deEscalate = async (id: string, reason: string) => {
    return ApiUtility.post<IApiResponse<string>>(
      `${this.route}/${id}/DeEscalate`,
      {
        reason,
        expectedVersion: 0,
      },
    );
  };

  getList = (
    start: number,
    length: number,
    status: number,
    search: string,
    sortCol: string,
    sortDir: string,
    priority?: number,
    dateType?: string,
    fromDate?: any,
    toDate?: any,
    type?: number,
    inputValue?: boolean | null,
    module?: TicketModule,
    closeReason?: number | string,
    practiceId?: number,
    agentId?: string,
  ) => {
    return ApiUtility.getResult(this.route, {
      start,
      length,
      status,
      priority,
      search,
      sortCol,
      sortDir,
      dateType,
      fromDate,
      toDate,
      type,
      inputValue,
      module,
      closeReason,
      practiceId,
      agentId,
      _t: Date.now(),
    });
  };

  get = (id: string) => {
    return ApiUtility.getResult(`${this.route}/${id}`, {
      _t: Date.now(),
    });
  };

  postReply = (id: string, formData: FormData) => {
    return ApiUtility.postForm<IApiResponse<boolean>>(`${this.route}/${id}/Reply`, formData);
  };

  reopen = async (id: string, reason: string) => {
    return ApiUtility.post<IApiResponse<string>>(`${this.route}/${id}/Reopen`, { reason });
  };

  close = async (id: string, reason: string, updateLinked?  : boolean) => {
    return ApiUtility.post<IApiResponse<string>>(`${this.route}/${id}/Close`, { reason, updateLinked });
  };

  changeStatus = async (id: string, data: { newStatus: number; reason: string; resolutionETA?: string; updateLinked?: boolean }) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/${id}/Status`, { ...data, expectedVersion: 0 });
  };

  updateAgent = async (id: string, userId: string) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/${id}/UpdateAgent`, { userId });
  };

  updateCollaborators = async(ticketIds:string[], userIds:string[]) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/UpdateCollaborators`, {ticketIds, userIds});
  }
  updatePriority = async (id: string, priority: number) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/${id}/Priority`, { priority });
  };



  updateModule = async (id: string, module: number) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/${id}/Module`, { module });
  };

  updateIssueType = async (id: string, issueType: number, reason: string) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/${id}/IssueType`, { issueType, reason });
  };

  setResolutionETA = async (id: string, resolutionETA: string, reason?: string) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/${id}/ResolutionETA`, { resolutionETA, reason });
  };

  setDueDate = async (id: string, dueDate: string, reason?: string) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/${id}/DueDate`, { dueDate, reason });
  };

  setInputRequired = async (id: string, required: boolean, reason?: string) => {

    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/${id}/InputRequired`, {
      required,
      reason,
    });
  };

  getEscalationHistory = async (id: string) => {
    return ApiUtility.getResult<IApiResponse<any[]>>(`${this.route}/${id}/EscalationHistory`, { _t: Date.now() });
  };

  getAttachmentUrl = async (ticketId: string, fileId: string) => {
    return ApiUtility.get<IApiResponse<any>>(`${this.route}/${ticketId}/Attachment`, { fileId, _t: Date.now() });
  };

  getThread = async (id: string) => {
    return ApiUtility.getResult<IApiResponse<ITicket>>(`${this.route}/${id}/Thread`, { _t: Date.now() });
  };

  mergeTickets = async (data: IMergeTicketRequest) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/Merge`, data);
  };

  unmergeTickets = async (id: string) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/Unmerge/${id}`, {});
  };

  unlinkTickets = async (id: string, removeAllInterconnected: boolean = false) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/Unlink/${id}?removeAllInterconnected=${removeAllInterconnected}`, {});
  };

  downloadTicketPdf = async (id: string, timeZone?: string) => {
    const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return ApiUtility.downloadFile(`${this.route}/${id}/Download`, {
      timeZone: tz,
      _t: Date.now(),
    });
  };

  downloadList = (
    status: number,
    search: string,
    sortCol: string,
    sortDir: string,
    priority?: number,
    type?: number,
    fromDate?: string,
    toDate?: string,
    inputValue?: boolean | null,
    closeReason?: number,
    practiceId?: number,
    agentId?: string,
    module?: TicketModule
  ) => {
    return ApiUtility.downloadFile(`${this.route}/Download`, {
      status,
      priority,
      search,
      sortCol,
      sortDir,
      type,
      fromDate,
      toDate,
      inputValue,
      closeReason,
      practiceId,
      agentId,
      module,
      _t: Date.now(),
    });
  };

  getCapsiTaskProjects = async (search: string = "", companyId: string = "", companyName: string = "") => {
    return ApiUtility.getResult<any[]>(`${this.route}/CapsiTask/Projects`, { search, companyId, companyName, _t: Date.now() });
  };

  getCapsiTaskAssignees = async (projectId: string, search: string = "") => {
    return ApiUtility.post<IApiResponse<any>>(`${this.route}/CapsiTask/Assignees`, {
      ids: [projectId],
      search,
      start: 0,
      length: 1000
    });
  };

  getCapsiTaskTypes = async () => {
    return ApiUtility.getResult<any[]>(`${this.route}/CapsiTask/Types`, { _t: Date.now() });
  };

  getCapsiTaskTags = async () => {
    return ApiUtility.getResult<any[]>(`${this.route}/CapsiTask/Tags`, { _t: Date.now() });
  };

  getCapsiTaskBranches = async () => {
    return ApiUtility.getResult<any[]>(`${this.route}/CapsiTask/Branches`, { _t: Date.now() });
  };

  shiftToCapsiTask = async (id: string, data: any) => {
    return ApiUtility.post<IApiResponse<boolean>>(`${this.route}/${id}/ShiftToCapsiTask`, data);
  };

  updateTicketStatus = (body: { ticketIds: string[]; status: number, closeReason?: number, customCloseReason?: string, updateLinked?: boolean}) => ApiUtility.post(`${this.route}/BulkUpdate`, body);
  linkTickets = (data: { ticketIds: string[] }) => ApiUtility.post<IApiResponse<boolean>>(`${this.route}/Link`, data);
}

export enum MergeType {
  CombineThreads = 1,
  SeparateThreads = 2,
}

export interface IMergeTicketRequest {
  parentTicketId?: string;
  childTicketIds?: string[];
  ticketId?: string;
  linkedTicketIds?: string[];
  parentStatus?: number;
  childStatus?: number;
  mergeType?: MergeType;
}

export interface ITicket {
  id: string;
  number: string;
  title: string;
  message: string;
  status: TicketStatus;
  isEscalated: boolean;
  module: TicketModule;
  priority: TicketPriority;
  slaPlan?: TicketPriority;
  issueType: number;
  version?: number;
  source?: string;
  
  // Timestamps & Metadata
  createdBy: {
    userId: string;
    userName: string;
    date: string;
  };
  
  // Relations
  company?: {
    id: string;
    name: string;
    value?: string;
  };
  
  sender?: {
    id: string;
    name: string;
  };
  assignedTo?: {
    id: string;
    name: string;
  };
  agent?: {
    id: string;
    name: string;
  };
  
  target?: {
    id: string;
    name: string;
  };
  
  collaborators?: Array<{
    id: string;
    name: string;
  }>;
  supportCollaborators?: Array<{
    id: string;
    name: string;
  }>;
  threads: ITicketThreadItem[];
  attachments?: any[];
  
  // Ticket State
  inputRequired?: boolean;
  resolutionETA?: string | null;
  dueDate?: string | null;
  ticketId?: string; // For escalated tickets
  
  // Resolution Details
  resolved?: {
    resolvedAt: string;
    resolutionETA?: string;
    resolvedBy?: {
      id: string;
      name: string;
    };
  };
  
  dependencies?: ITicketDependency[];
  reopenedAt?:string,
  capsiTaskRef?: {
    id: string;
    name: string;
  };
}

export interface ICreateTicket {
  title: string;
  message: string;
  module: TicketModule;
  priority: TicketPriority;
  issueType?: number;
  phone?: string;
  practiceId?: string;
  sender?: {
    name: string;
    email: string;
    phone: string;
  };
  attachments?: any[];
}

export interface ITicketDependency {
  id: string;
  name: string;
  type: TicketDependencyType;
  ticketRef?: string;
}

export enum TicketDependencyType {
  Unknown = 0,
  Merged = 1,
  Linked = 2,
}
export interface ITicketActivity {
  ticketId: string;
  fieldName: string;
  from: string;
  to: string;
}

export interface ITicketThreadItem {
  id: string;
  posterType: TicketPosterType;
  body: string;
  createdBy: {
    date: string;
    userId:string;
    userName: string
  };
  isSystem: boolean;
  isPrivate?: boolean;
  attachments?: any[];
  activity?: ITicketActivity[] | ITicketActivity;
  ticketRef?: string;
}
export enum TicketPosterType {
  Client = 1,
  PracticeStaff = 2,
  Admin = 3,
}

export default new TicketService();
